import { CUSTOM_ATTRIBUTES_PREFIX, EXTERNAL_ID, Method } from 'consts';
import { useCallback } from 'react';
import { useQuery } from 'react-query';
import {
	IResAttribute,
	IResRoles,
	IResUserAttributes,
	IUserAttributes
} from 'types/api';
import { get } from './api';
import { API_ROUTES } from './apiRoutes';
import { useMandatoryUserAttributes } from './configurations';
import QueryKeys from './queryKeys';

const getUserAttributes = async () => {
	const res = await get<IResUserAttributes>(API_ROUTES.userAttributes);
	return res.data;
};

const isMandatoryAttribute = (
	attr: IResAttribute,
	mandatoryUserAttributes: string[]
): boolean =>
	attr.custom
		? mandatoryUserAttributes.includes(
				`${CUSTOM_ATTRIBUTES_PREFIX}${attr.value}`
			)
		: [...mandatoryUserAttributes, EXTERNAL_ID].includes(attr.value);

const selectUserAttributesWithMandatory = (
	userAttributes: IResUserAttributes,
	mandatoryUserAttributes: string[]
) =>
	userAttributes.map((attribute) => ({
		label: attribute.label,
		value: attribute.value,
		custom: attribute.custom,
		required: isMandatoryAttribute(attribute, mandatoryUserAttributes)
	})) as IUserAttributes;

export const useMethodUserAttributes = (method: Method | null) => {
	const { data: mandatoryUserAttributes } = useMandatoryUserAttributes(method);

	const selectUserAttributes = useCallback(
		(attributes: IResUserAttributes) =>
			selectUserAttributesWithMandatory(
				attributes,
				mandatoryUserAttributes || []
			).filter(
				(attribute) =>
					!(method === Method.Saml && attribute.value === EXTERNAL_ID)
			),
		[mandatoryUserAttributes, method]
	);

	return useQuery({
		queryKey: QueryKeys.userAttributes,
		queryFn: getUserAttributes,
		enabled: !!mandatoryUserAttributes,
		select: selectUserAttributes
	});
};

const getRoles = async () => {
	const res = await get<IResRoles>(API_ROUTES.roles);

	return res.data;
};

export const useRoles = () =>
	useQuery({
		queryKey: QueryKeys.roles,
		queryFn: getRoles
	});
